import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Input,
  Layout,
  Result,
  Skeleton,
  Table,
  Tag,
  notification,
  theme,
} from "antd";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiSubscriptionList } from "../../../../api/subscription";
import { userHasPermission } from "../../../../helpers/user";
import "./SubscriptionListing.scss";

const { Content } = Layout;

const DEBOUNCE_DELAY = 500;

const frequencyLabels = {
  weekly: "Semanal",
  biweekly: "Quinzenal",
  monthly: "Mensal",
  bimonthly: "Bimestral",
  quarterly: "Trimestral",
  semiannual: "Semestral",
  annual: "Anual",
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

const SubscriptionListing = () => {
  const navigate = useNavigate();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const debounceTimerRef = useRef(null);

  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });

  const fetchSubscriptions = useCallback(async (page, search = "") => {
    setLoading(true);
    try {
      const params = { page };
      if (search) {
        params.search = search;
      }
      const json = await apiSubscriptionList(params);
      setFilteredData(json.results);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count,
        current: page,
      }));
    } catch (error) {
      setActiveError(true);
      if (error?.status === 400) {
        notification.error({
          message: "Erro",
          description: "Ocorreu um erro ao listar as assinaturas.",
          placement: "topRight",
        });
        setErrorStatus("404");
      } else if (error?.status === 500) {
        notification.error({
          message: "Erro",
          description:
            "Ocorreu um erro no servidor. Por favor, tente novamente mais tarde.",
          placement: "topRight",
        });
        setErrorStatus("500");
      } else {
        setErrorStatus("404");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchSubscriptions(1, value);
    }, DEBOUNCE_DELAY);
  };

  const handleSearchSubmit = (value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    fetchSubscriptions(1, value);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    fetchSubscriptions(1, "");
  };

  const onChange = (paginationConfig) => {
    fetchSubscriptions(paginationConfig.current, searchValue);
  };

  useEffect(() => {
    fetchSubscriptions(1);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [fetchSubscriptions]);

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  const columns = [
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      render: (name) => (
        <span style={{ fontWeight: 500 }}>{name}</span>
      ),
    },
    {
      title: "Valor",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => formatCurrency(amount),
      responsive: ["md"],
    },
    {
      title: "Frequencia",
      dataIndex: "frequency",
      key: "frequency",
      render: (frequency) => (
        <Tag color="blue">{frequencyLabels[frequency] || frequency}</Tag>
      ),
      responsive: ["sm"],
    },
    {
      title: "Total Assinantes",
      dataIndex: "total_subscribers",
      key: "total_subscribers",
      align: "center",
      render: (total) => (
        <span className="subscriber-count">{total}</span>
      ),
    },
    {
      title: "Assinantes Ativos",
      dataIndex: "active_subscribers",
      key: "active_subscribers",
      align: "center",
      render: (active, record) => (
        <span className={`subscriber-count ${active > 0 ? "active" : ""}`}>
          {active}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "",
      key: "actions",
      width: 50,
      render: (_, record) => (
        <Link to={`/assinatura/${record.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", fontSize: 16 }} />
        </Link>
      ),
    },
  ];

  const locale = {
    emptyText: <Empty description="Nenhuma assinatura encontrada" />,
  };

  return (
    <div className="SubscriptionListing">
      {activeError ? (
        renderError(errorStatus)
      ) : (
        <Layout style={{ padding: "1.25rem" }}>
          <Breadcrumb style={{ margin: "16px 0" }}>
            <div
              style={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 className="title-page">Assinaturas</h2>
            </div>
            <Divider />
            <Breadcrumb.Item>Assinaturas</Breadcrumb.Item>
            <Breadcrumb.Item>Listar Assinaturas</Breadcrumb.Item>
          </Breadcrumb>
          <Content
            style={{
              padding: 24,
              margin: 0,
              minHeight: 280,
              background: colorBgContainer,
              borderRadius: borderRadiusLG,
            }}
          >
            <div className="box-divider">
              <h2 className="subtitle-page">Planos de Assinatura</h2>
              {userHasPermission("add_subscription_plan") && <Button
                onClick={() => navigate("/assinatura/criar")}
                type="primary"
                icon={<PlusOutlined />}
              >
                Nova Assinatura
              </Button>}
            </div>
            <Divider />
            <Input.Search
              placeholder="Pesquisar"
              allowClear
              enterButton
              value={searchValue}
              onSearch={handleSearchSubmit}
              onChange={handleSearchChange}
              onClear={handleClearSearch}
              style={{ marginBottom: 16, maxWidth: "200px" }}
            />
            {loading ? (
              <Skeleton active />
            ) : (
              <Table
                columns={columns}
                locale={locale}
                dataSource={
                  filteredData &&
                  filteredData.map((item) => ({ ...item, key: item.id }))
                }
                pagination={{
                  current: pagination.current,
                  total: pagination.total,
                  showSizeChanger: false,
                }}
                bordered
                onChange={onChange}
              />
            )}
          </Content>
          <Divider />
        </Layout>
      )}
    </div>
  );
};

export default SubscriptionListing;
